import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_CONFIGURATION_API ?? 'http://localhost:8160';

export type ConfigurationDashboard = {
  total_items: number;
  total_flags: number;
  enabled_flags: number;
  total_secrets: number;
  published_version: number;
  recent_activity?: ActivityEntry[];
};

export type ActivityEntry = {
  id: string;
  tenant_id: string;
  action: string;
  resource: string;
  details?: string;
  actor: string;
  created_at: string;
};

export type ConfigurationItem = {
  id: string;
  key: string;
  value: unknown;
  value_type?: string;
  scope: string;
  scope_id?: string;
  category: string;
  module_id?: string;
  version: number;
  status: string;
  created_at: string;
  updated_at: string;
};

export type FeatureFlag = {
  id: string;
  key: string;
  name: string;
  description?: string;
  type: string;
  enabled: boolean;
  default_value: boolean;
  rollout_percent?: number;
  kill_switch?: boolean;
  license_feature?: string;
  created_at: string;
  updated_at: string;
};

export type ResolvedConfig = {
  tenant_id: string;
  scope: string;
  values: Record<string, unknown>;
  flags: Record<string, boolean>;
  resolved_at: string;
};

export type LocalizationProfile = {
  id?: string;
  tenant_id?: string;
  language: string;
  timezone: string;
  currency: string;
  date_format: string;
  number_format: string;
  country?: string;
  region?: string;
};

export type SecretRecord = {
  id: string;
  tenant_id: string;
  key: string;
  version: number;
  active: boolean;
  rotated_at: string;
};

export type ConfigurationVersion = {
  id: string;
  tenant_id: string;
  version: number;
  status: string;
  published_at?: string;
  created_at: string;
};

function headers() {
  return { 'X-Tenant-ID': 'default', 'X-Organization-ID': 'default' };
}

export const configurationApi = {
  dashboard: () => fetchAPI<ConfigurationDashboard>(`${BASE}/api/v1/configuration/dashboard`, { headers: headers() }),

  listConfig: () => fetchAPI<{ items: ConfigurationItem[] }>(`${BASE}/api/v1/configuration`, { headers: headers() }),

  createConfig: (body: { key: string; value: unknown; scope: string; category: string; scope_id?: string }) =>
    fetchAPI<ConfigurationItem>(`${BASE}/api/v1/configuration`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }),

  updateConfig: (id: string, body: { value: unknown }) =>
    fetchAPI<ConfigurationItem>(`${BASE}/api/v1/configuration/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) }),

  resolved: (moduleId?: string) =>
    fetchAPI<ResolvedConfig>(`${BASE}/api/v1/configuration/resolved${moduleId ? `?module_id=${moduleId}` : ''}`, { headers: headers() }),

  listFlags: () => fetchAPI<{ flags: FeatureFlag[] }>(`${BASE}/api/v1/feature-flags`, { headers: headers() }),

  createFlag: (body: { key: string; name: string; type: string; enabled: boolean; default_value: boolean; rollout_percent?: number }) =>
    fetchAPI<FeatureFlag>(`${BASE}/api/v1/feature-flags`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }),

  updateFlag: (id: string, body: { enabled?: boolean; rollout_percent?: number; kill_switch?: boolean }) =>
    fetchAPI<FeatureFlag>(`${BASE}/api/v1/feature-flags/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) }),

  flagEnabled: (key: string) =>
    fetchAPI<{ enabled: boolean }>(`${BASE}/api/v1/feature-flags/enabled?key=${encodeURIComponent(key)}`, { headers: headers() }),

  listPreferences: () => fetchAPI<{ preferences: unknown[] }>(`${BASE}/api/v1/preferences`, { headers: headers() }),

  updatePreferences: (body: { category: string; values: Record<string, unknown> }) =>
    fetchAPI<{ preferences: unknown[] }>(`${BASE}/api/v1/preferences`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) }),

  listSecrets: () => fetchAPI<{ secrets: SecretRecord[] }>(`${BASE}/api/v1/configuration/secrets`, { headers: headers() }),

  createSecret: (body: { key: string; value: string }) =>
    fetchAPI<{ id: string; key: string; version: number }>(`${BASE}/api/v1/configuration/secrets`, { method: 'POST', headers: headers(), body: JSON.stringify(body) }),

  getLocalization: () => fetchAPI<LocalizationProfile>(`${BASE}/api/v1/configuration/localization`, { headers: headers() }),

  updateLocalization: (body: LocalizationProfile) =>
    fetchAPI<LocalizationProfile>(`${BASE}/api/v1/configuration/localization`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) }),

  exportConfig: () => fetchAPI<unknown>(`${BASE}/api/v1/configuration/export`, { headers: headers() }),

  importConfig: (payload: unknown) =>
    fetchAPI<{ status: string }>(`${BASE}/api/v1/configuration/import`, { method: 'POST', headers: headers(), body: JSON.stringify(payload) }),

  publish: () => fetchAPI<ConfigurationVersion>(`${BASE}/api/v1/configuration/publish`, { method: 'POST', headers: headers() }),

  history: () => fetchAPI<{ versions: ConfigurationVersion[] }>(`${BASE}/api/v1/configuration/history`, { headers: headers() }),

  compare: (v1: number, v2: number) =>
    fetchAPI<{ diff: Record<string, unknown> }>(`${BASE}/api/v1/configuration/compare?v1=${v1}&v2=${v2}`, { headers: headers() }),

  runtimeReload: () => fetchAPI<{ status: string }>(`${BASE}/api/v1/runtime/reload`, { method: 'POST', headers: headers() }),
};
